import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ExecutionResult, LcsApiService } from '@lcs/shared/api';
import {
   UserCallRecordingExtensionGroupInput,
   UserCallRecordingExtensionInput,
   UserPostInput,
   UserPrivilegeInput,
} from 'generated/graphql-types';
import { Observable } from 'rxjs';
import { handleUserReports } from 'src/app/common-components/helpers/role-and-user-reports-helper';
import { UserDetailSaveModel, UserPrivilegeSaveModel, UserRoleSaveModel } from '../models/user-detail-save.model';
import { UserDetailView } from '../models/user-detail-view.model';
import { UserVerificationEmailRequestModel } from '../models/user-verification-email-request.model';
import { SaveActiveUserMutation } from '../queries/archive-user.graphql';
import { GetUserDetail_user_rolesConnection_items } from '../queries/generated/GetUserDetail';
import { SaveActiveUser, SaveActiveUserVariables } from '../queries/generated/SaveActiveUser';
import { SaveLockedUser, SaveLockedUserVariables } from '../queries/generated/SaveLockedUser';
import { SaveUser, SaveUserVariables } from '../queries/generated/SaveUser';
import { SaveUserMutation } from '../queries/save-user.graphql';
import { SaveLockedUserMutation } from '../queries/user-unlock.graphql';

type UserSaveResult = ExecutionResult<SaveUser>;

@Injectable({ providedIn: 'root' })
export class UserDetailApiService {
   constructor(private apiService: LcsApiService, private httpClient: HttpClient) {}

   save(
      userDetail: UserDetailSaveModel,
      fields: (keyof UserPostInput)[],
      userPrivilegeFields: string[],
      userCallRecordingExtensionFields: (keyof UserCallRecordingExtensionInput)[],
      userCallRecordingExtensionGroupFields: (keyof UserCallRecordingExtensionGroupInput)[],
      existingUser: UserDetailView | null,
      isSystem = false
   ): Observable<ExecutionResult<SaveUser>> {
      const user = this.createUserInput(
         userDetail,
         fields,
         userCallRecordingExtensionFields,
         userCallRecordingExtensionGroupFields
      );
      const userPrivileges = this.createUserPrivilegesInput(userDetail, userPrivilegeFields, isSystem);
      let deletedUserRoleIds: number[] = [];

      deletedUserRoleIds = getDeletedUserRoleIds(userDetail.roles ?? [], existingUser?.rolesConnection?.items ?? []);

      let deletedUserReportIds: number[] = userDetail.userReports ? userDetail.userReports['removed'] : [];

      deletedUserReportIds = deletedUserReportIds || [];

      const hasDeletedUserReports = deletedUserReportIds && deletedUserReportIds.length > 0;

      const deletedUserPrivilegeIds = getDeletedUserPrivilegeIds(userDetail.userPrivileges || []);

      const deletedUserCallRecordingExtensionIds =
         userDetail.userCallExtensionRecordingPrivileges
            ?.filter((priv) => priv.isDeleted)
            .map((p) => p.userCallRecordingExtensionId) ?? [];

      const deletedUserCallRecordingExtensionGroupIds =
         userDetail.userCallExtensionGroupRecordingPrivileges
            ?.filter((priv) => priv.isDeleted)
            .map((p) => p.userCallRecordingExtensionGroupId) ?? [];

      const hasDeletedCallRecordingExtensions = deletedUserCallRecordingExtensionIds.length > 0;

      const hasDeletedCallRecordingExtensionGroups = deletedUserCallRecordingExtensionGroupIds.length > 0;

      const variables: SaveUserVariables = {
         deletedUserRoleIds: deletedUserRoleIds,
         deletedUserReportIds: deletedUserReportIds,
         deletedUserPrivilegeIds: deletedUserPrivilegeIds,
         deletedUserCallRecordingExtensionIds: deletedUserCallRecordingExtensionIds,
         deletedUserCallRecordingExtensionGroupIds: deletedUserCallRecordingExtensionGroupIds,
         userPrivileges: userPrivileges,
         hasDeletedUserRoles: deletedUserRoleIds.length > 0,
         user: user,
         hasDeletedUserReports: hasDeletedUserReports,
         hasDeletedUserPrivileges: deletedUserPrivilegeIds.length > 0,
         hasDeletedCallRecordingExtensions,
         hasDeletedCallRecordingExtensionGroups,
      };

      return this.apiService.query<SaveUser, SaveUserVariables>({
         query: SaveUserMutation,
         variables: variables,
      });
   }

   deleteUser(userId: number): Observable<ExecutionResult<SaveActiveUser>> {
      return this.apiService.query<SaveActiveUser, SaveActiveUserVariables>({
         query: SaveActiveUserMutation,
         variables: {
            user: {
               userId: userId,
               isActive: false,
               fields: ['userId', 'isActive'],
            },
         },
      });
   }

   resendVerificationEmail(body: UserVerificationEmailRequestModel): Observable<HttpResponse<void>> {
      return this.httpClient.post<void>(this.apiService.apiUrl + '/Signup/SendVerificationEmail', body, {
         observe: 'response',
      });
   }

   unlockUser(userId: number): Observable<ExecutionResult<SaveLockedUser>> {
      return this.apiService.query<SaveLockedUser, SaveLockedUserVariables>({
         query: SaveLockedUserMutation,
         variables: {
            user: {
               userId: userId,
               isLocked: false,
               fields: ['userId', 'isLocked'],
            },
         },
      });
   }

   private createUserInput(
      user: UserDetailSaveModel,
      userFields: (keyof UserPostInput)[],
      userCallRecordingExtensionFields: (keyof UserCallRecordingExtensionInput)[],
      userCallRecordingExtensionGroupFields: (keyof UserCallRecordingExtensionGroupInput)[]
   ): UserPostInput {
      userFields = [...userFields, 'userId'];

      const fields: string[] = userFields;

      const userInput: UserPostInput = { fields, userId: user.userId, userReports: null };

      handleUserReports(user, userInput, userFields);

      userFields.forEach((field) => {
         if (field === 'roles') {
            userInput[field.toString()] = user[field]?.map((role: UserRoleSaveModel) => {
               return {
                  fields: [],
                  roleId: role.roleId,
                  userId: role.userId,
                  userRoleId: role.userRoleId,
               };
            });
            userInput.fields.push('roles.userId');
            userInput.fields.push('roles.roleId');
            userInput.fields = userInput.fields.filter((f) => {
               return f !== 'roles'; // replaced with roles.userId and roles.roleId above
            });
         } else {
            userInput[field.toString()] = user[field];
         }
      });

      if (userCallRecordingExtensionGroupFields.length > 0) {
         userInput['userCallRecordingExtensions'] = createUserCallRecordingExtensionInput(user);
         formatUserCallRecordingExtensionFields(
            userInput.fields,
            'userCallRecordingExtensions',
            userCallRecordingExtensionFields
         );
      }
      if (userCallRecordingExtensionFields.length > 0) {
         userInput['userCallRecordingExtensionGroups'] = createUserCallRecordingExtensionGroupInput(user);
         formatUserCallRecordingExtensionGroupFields(
            userInput.fields,
            'userCallRecordingExtensionGroups',
            userCallRecordingExtensionGroupFields
         );
      }

      return userInput;
   }

   private createUserPrivilegesInput(user: UserDetailSaveModel, userPrivilegeFields: string[], isSystem = false) {
      const userPrivileges: UserPrivilegeInput[] = [];
      const uniquePrivilegeIds: number[] = [];
      userPrivilegeFields.forEach((field) => {
         let privilegeId: number = +field.split('.')[0];
         let userPrivilegeField: string = field.split('.')[1];
         if (!uniquePrivilegeIds.includes(privilegeId)) {
            uniquePrivilegeIds.push(privilegeId);
            const userPrivilege: UserPrivilegeInput = {
               fields: [
                  'isAdd',
                  'isView',
                  'isDelete',
                  'isEdit',
                  'isEnabled',
                  'userId',
                  'privilegeId',
                  'userPrivilegeId',
                  'accountId',
               ],
               userId: user.userId,
               privilegeId: privilegeId,
               accountId: isSystem ? null : user.accountId,
               userPrivilegeId: user.userPrivileges?.find((userPrivilege) => userPrivilege.privilegeId === privilegeId)
                  ?.userPrivilegeId,
               isAdd:
                  (user.userPrivileges?.find((userPrivilege) => userPrivilege.privilegeId === privilegeId) || {})[
                     'isAdd'
                  ] ?? false,
               isDelete:
                  (user.userPrivileges?.find((userPrivilege) => userPrivilege.privilegeId === privilegeId) || {})[
                     'isDelete'
                  ] ?? false,
               isEdit:
                  (user.userPrivileges?.find((userPrivilege) => userPrivilege.privilegeId === privilegeId) || {})[
                     'isEdit'
                  ] ?? false,
               isEnabled:
                  (user.userPrivileges?.find((userPrivilege) => userPrivilege.privilegeId === privilegeId) || {})[
                     'isEnabled'
                  ] ?? false,
               isView:
                  (user.userPrivileges?.find((userPrivilege) => userPrivilege.privilegeId === privilegeId) || {})[
                     'isView'
                  ] ?? false,
            };
            if (
               userPrivilege.isAdd ||
               userPrivilege.isView ||
               userPrivilege.isEdit ||
               userPrivilege.isDelete ||
               userPrivilege.isEnabled
            ) {
               userPrivileges.push(userPrivilege);
            }
         }
      });
      return userPrivileges;
   }
}

export function getDeletedUserRoleIds(
   currentUserRoles: UserRoleSaveModel[],
   existingUserRoles: GetUserDetail_user_rolesConnection_items[]
): number[] {
   if (!currentUserRoles || !existingUserRoles) {
      return [];
   }
   return existingUserRoles
      .filter((userRole) => {
         return !currentUserRoles.some((currentUserRole) => {
            return currentUserRole.userRoleId === userRole.userRoleId;
         });
      })
      .map((deletedUserRole) => {
         return deletedUserRole.userRoleId;
      });
}

export function getDeletedUserPrivilegeIds(userPrivileges: UserPrivilegeSaveModel[]): number[] {
   return userPrivileges
      .filter(
         (priv) =>
            !priv.isAdd &&
            !priv.isView &&
            !priv.isEdit &&
            !priv.isDelete &&
            !priv.isEnabled &&
            priv.userPrivilegeId !== 0
      )
      .map((priv) => priv.userPrivilegeId as number);
}

function formatUserCallRecordingExtensionFields(
   fields: string[],
   key: string,
   embedFields: (keyof UserCallRecordingExtensionInput)[]
) {
   for (const embed of embedFields) {
      fields.push(`${key}.${embed}`);
   }
}

function formatUserCallRecordingExtensionGroupFields(
   fields: string[],
   key: string,
   embedFields: (keyof UserCallRecordingExtensionGroupInput)[]
) {
   for (const embed of embedFields) {
      fields.push(`${key}.${embed}`);
   }
}

function createUserCallRecordingExtensionInput(user: UserDetailSaveModel): UserCallRecordingExtensionInput[] {
   let userCallRecordingExtensionInput: UserCallRecordingExtensionInput[];
   userCallRecordingExtensionInput =
      user.userCallExtensionRecordingPrivileges
         ?.filter((priv) => {
            return !priv.isDeleted;
         })
         .map((privilege) => {
            return {
               userId: privilege.userId,
               extensionId: privilege.extensionId,
               isInclude: privilege.isInclude,
               userCallRecordingExtensionId: privilege.userCallRecordingExtensionId,
               fields: [],
            };
         }) ?? [];
   return userCallRecordingExtensionInput;
}

function createUserCallRecordingExtensionGroupInput(user: UserDetailSaveModel): UserCallRecordingExtensionGroupInput[] {
   let userCallRecordingExtensionGroupInput: UserCallRecordingExtensionGroupInput[];
   userCallRecordingExtensionGroupInput =
      user.userCallExtensionGroupRecordingPrivileges
         ?.filter((priv) => {
            return !priv.isDeleted;
         })
         .map((privilege) => {
            return {
               userId: privilege.userId,
               extensionGroupId: privilege.extensionGroupId,
               isInclude: privilege.isInclude,
               userCallRecordingExtensionGroupId: privilege.userCallRecordingExtensionGroupId,
               fields: [],
            };
         }) ?? [];
   return userCallRecordingExtensionGroupInput;
}
